<template>
  <div class="geo-item-search junkai-tenken-report-tenken-data">
    <GeoItemSearchCommon
      v-model:search-params="commonSearchParams"
      :checkbox-key-prefix="checkboxKeyPrefix"
      :is-kp-search="false"
      :default-days-before="7"
      @change="onChange"
    />
    <div class="tenken-kubun">
      <span class="title-area" @click="state.showTenkenKubun = !state.showTenkenKubun">
        <i class="fas mr4" :class="{
          'fa-caret-right': !state.showTenkenKubun,
          'fa-caret-down': state.showTenkenKubun,
        }"></i>
        点検区分
      </span>
      <div v-for="key of Object.keys(state.groupedTenkenKubuns)"
          :key="key"
          class="ml12"
          v-show="state.showTenkenKubun">
        <span class="title-area" @click="state.showGroupedTenkenKubuns[key] = !state.showGroupedTenkenKubuns[key]">
          <i class="fas mr4" :class="{
            'fa-caret-right': !state.showGroupedTenkenKubuns[key],
            'fa-caret-down': state.showGroupedTenkenKubuns[key],
          }"></i>
          {{ key }}
        </span>
        <div class="custom-control custom-checkbox ml15"
            v-for="(tenkenKubun, tenkenKubun_idx) in state.groupedTenkenKubuns[key]"
            :key="tenkenKubun_idx"
            v-show="state.showGroupedTenkenKubuns[key]">
          <input type="checkbox" class="custom-control-input"
                :id="`${checkboxKeyPrefix}-${key}-${tenkenKubun_idx}`"
                :value="tenkenKubun"
                v-model="state.search.tenkenKubuns"
                @change="onChange">
          <label class="custom-control-label" :for="`${checkboxKeyPrefix}-${key}-${tenkenKubun_idx}`">
            {{tenkenKubun.tenkenKubunName}}
          </label>
        </div>
      </div>
    </div>
    <div class="hantei" v-if="state.hanteis.length > 0">
      <span class="title-area" @click="state.showHantei = !state.showHantei">
        <i class="fas mr4" :class="{
          'fa-caret-right': !state.showHantei,
          'fa-caret-down': state.showHantei,
        }"></i>
        判定
      </span>
      <div class="custom-control custom-checkbox ml15"
          v-for="e in state.hanteis"
          :key="e.id"
          v-show="state.showHantei">
        <input type="checkbox" class="custom-control-input"
          :id="`${checkboxKeyPrefix}-hantei-${e.id}`"
          :value="e"
          v-model="state.search.hanteis"
          @change="onChange">
        <label class="custom-control-label" :for="`${checkboxKeyPrefix}-hantei-${e.id}`">
          {{e.hantei}}
        </label>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  onMounted,
  reactive,
  ref,
} from 'vue';
import type { JunkaiTenkenIndexParams } from 'src/models/apis/extJunkaiTenkenReportTenkenData/extJunkaiTenkenReportTenkenDataRequest';
import GeoItemSearchCommon from 'src/components/Top/geoItemSearchComponents/GeoItemSearchCommon.vue';
import type { CommonSearchParams } from 'src/models/geoItemSearch';
import extJunkaiTenkenReportTenkenDataApi from 'src/apis/extJunkaiTenkenReportTenkenData';
import type {
  TenkenKubun,
  Hantei,
} from 'src/models/extJunkaiTenkenReportTenkenData';

interface SearchParams {
  tenkenKubuns: TenkenKubun[];
  hanteis: Hantei[];
}

interface GeoItemSearchJunkaiTenkenReportTenkenDataState {
  search: SearchParams;
  showTenkenKubun: boolean;
  showGroupedTenkenKubuns: Record<string, boolean>;
  groupedTenkenKubuns: Record<string, TenkenKubun[]>;
  showHantei: boolean;
  hanteis: Hantei[];
}

const checkboxKeyPrefix = 'tenken';

const emit = defineEmits<{
  (e: 'change'): void;
}>();

const initSearchState = (): SearchParams => {
  return {
    tenkenKubuns: [],
    hanteis: [],
  };
};
const state = reactive<GeoItemSearchJunkaiTenkenReportTenkenDataState>({
  search: initSearchState(),
  showTenkenKubun: false,
  showGroupedTenkenKubuns: {},
  groupedTenkenKubuns: {},
  showHantei: false,
  hanteis: [],
});
const getGroupedTenkenKubuns = (tenkenKubuns: TenkenKubun[]): Record<string, TenkenKubun[]> => {
  // 点検区分一覧データを「路上・土木」、「路上・施設」、「路下・土木」、「路下・施設」、「高架下」...ごとにグループ化する
  const results = Object.values(tenkenKubuns)
    .reduce((acc: Record<string, TenkenKubun[]>, tenkenKubun) => {
      const baseName = tenkenKubun.tenkengyomuBaseName.replace(/巡回点検$/, '');
      const detailName = tenkenKubun.tenkengyomuDetailName.replace(/電気関係$|関係$/, '');
      const key = (baseName === detailName) ? baseName : `${baseName}・${detailName}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(tenkenKubun);
      return acc;
    }, {});

  return results;
};

onMounted(async() => {
  try {
    const masterData = await extJunkaiTenkenReportTenkenDataApi.getTenkenMaster();
    state.groupedTenkenKubuns = getGroupedTenkenKubuns(masterData.tenkenKubuns);
    state.showGroupedTenkenKubuns = Object.keys(state.groupedTenkenKubuns)
      .reduce((acc: Record<string, boolean>, key) => {
        acc[key] = false;
        return acc;
      }, {});
    state.hanteis = masterData.hanteis;
  } catch {
    // マスターデータ取得失敗時は検索条件を空のまま表示する（致命的ではないため画面はブロックしない）
  }
});

const commonSearchParams = ref<CommonSearchParams | null>(null);
const getSearchParams = (): JunkaiTenkenIndexParams | null => {
  if (!commonSearchParams.value) return null;

  const { tsFrom: dtFrom, tsTo: dtTo, ...other } = commonSearchParams.value;
  return {
    dt_from: dtFrom,
    dt_to: dtTo,
    road_names: other.roadNameDisps,
    directions: other.directions,
    tenken_kubuns: state.search.tenkenKubuns.map(e => e.id),
    hanteis: state.search.hanteis.map(e => e.id),
  };
};
const onChange = () => {
  emit('change');
};

defineExpose({ getSearchParams });
</script>

<style lang="scss" scoped>
.geo-item-search {
  &.junkai-tenken-report-tenken-data {
    margin: 0 0 0 25px;
  }
}
.title-area {
  cursor: pointer;
}
</style>
